import { Component, inject, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SharedStorageService } from '../../shared/services/shared-storage.service';
import { API_PARAMS } from '../../shared/constants/api.constants';
import { MatError } from '@angular/material/select';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle,
} from '@angular/material/datepicker';
import { MatInput } from '@angular/material/input';
import {
  API_DATE_FORMAT,
  DATE_FORMAT,
} from '../../shared/constants/date.constants';
import moment from 'moment/moment';
import { MatButton } from '@angular/material/button';
import { Router } from '@angular/router';
import { NgClass, NgOptimizedImage } from '@angular/common';
import { DateInputDirective } from '../../directives/date-input.directive';
import { isNumber } from 'node:util';
import { ID_LOGIN_TYPE } from '../../shared/constants/common.constants';
import { MatDialog } from '@angular/material/dialog';
import { SingleActionDialogComponent } from '../../shared/components/dialogs/single-action-dialog/single-action-dialog.component';
import { birthdayValidator, calculateAge } from '../../shared/utils/validator.util';

/**
 * 填寫個人基本資料
 */
@Component({
  selector: 'app-health-plan',
  standalone: true,
  imports: [
    TranslatePipe,
    FormsModule,
    MatRadioGroup,
    MatRadioButton,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatInput,
    ReactiveFormsModule,
    MatButton,
    MatError,
    NgClass,
    DateInputDirective,
    NgOptimizedImage,
  ],
  templateUrl: './health-plan.component.html',
  styleUrl: './health-plan.component.scss',
})
export class HealthPlanComponent implements OnInit {
  translateService: TranslateService = inject(TranslateService);
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  router: Router = inject(Router);
  dialog: MatDialog = inject(MatDialog);

  showError: boolean = false;

  form = new FormGroup({
    gender: new FormControl<string>('', [Validators.required]),
    birthday: new FormControl<moment.Moment | null>(null, [
      Validators.required,
      (control: FormControl) =>
        birthdayValidator(control, this.translateService, {
          max: 75,
          min: 18,
        }, calculateAge),
    ]),
    status: new FormControl<string>(''),
    menopausal: new FormControl<string>(''),
  });

  ngOnInit() {
    // 將當前預約步驟設置為2
    this.sharedStorageService.set('reserveStep', 2);

    // 標記為特殊活動頁面
    this.sharedStorageService.set('healthPlan', 'special');

    // 監聽gender變化時，添加/刪除女性相關欄位校驗
    this.form.get('gender')?.valueChanges.subscribe(value => {
      // 當 gender 變動時執行
      if (this.isFemale) {
        this.form.get('status')?.setValidators([Validators.required]);
        this.form.get('menopausal')?.setValidators([Validators.required]);
      } else {
        this.form.get('status')?.clearValidators();
        this.form.get('menopausal')?.clearValidators();
      }
      this.form.get('status')?.updateValueAndValidity();
      this.form.get('menopausal')?.updateValueAndValidity();
    });

    const userPlanRequest = this.sharedStorageService.get('userPlanRequest');
    const loginStatus = this.sharedStorageService.get('loginStatus');

    // 只有 memberLogin 才算有 web 帳號
    const hasWebAccount = loginStatus === 'memberLogin';
    const hasFilledReserveInfo = this.sharedStorageService.get('hasFilledReserveInfo');

    // 只有有 web 帳號或已填寫過時才預帶
    const shouldPreFill = hasFilledReserveInfo || hasWebAccount;

    // 性別預帶邏輯
    if (
      shouldPreFill &&
      userPlanRequest?.gender !== undefined &&
      userPlanRequest?.gender !== null &&
      userPlanRequest?.gender !== ''
    ) {
      this.form.get('gender')?.setValue(userPlanRequest?.gender);
    }
    
    // 生日預帶邏輯
    if (
      shouldPreFill &&
      userPlanRequest?.birthday !== undefined &&
      userPlanRequest?.birthday !== null &&
      userPlanRequest?.birthday !== ''
    ) {
      this.form
        .get('birthday')
        ?.setValue(moment(userPlanRequest?.birthday, API_DATE_FORMAT));
    }
  }

  // 根據輸入的生日計算年齡
  get age(): string {
  const birthday = this.form.get('birthday')?.value;
  if (!birthday) return '--';  // 簡化：calculateAge 內會處理無效
  
  const ageNum = calculateAge(birthday);  // 直接呼叫，支援 string | moment.Moment
  return ageNum > 0 ? String(ageNum) : '--';
}

  get isFemale(): boolean {
    return this.form?.get('gender')?.value === API_PARAMS.GENDER.FEMALE;
  }

  // 獲取驗證生日錯誤訊息
  getBirthdayErrorMessage() {
    const control = this.form.get('birthday');
    if (!control || !control.errors || !this.showError) return '';

    const errors = control.errors;

    // mat-datepickerParse錯誤
    if (errors['matDatepickerParse']) {
      return this.translateService.instant('error.birthday-error.format');
    }
    if (errors['required']) {
      return this.translateService.instant('error.birthday-error.required');
    }
    if (errors['invalidFormat']) {
      return errors['invalidFormat'];
    }
    if (errors['invalidAge']) {
      return ` <div class="input-calculator-msg input-mobile-msg">
          </div><div class="input-warn-msg">
        <img width="16" height="16" src="/assets/img/i-warn-outline.svg" alt=""/><span>${this.translateService.instant('error.birthday-error.reserve-age-invalid')}</span></div>`;
    }
    return '';
  }

  // 點擊上一步
  handlePreviousClick() {
    this.router.navigate(['/healthcheck/reserve/location']).then();
  }

  // 點擊下一步
  onSubmit() {
    this.showError = true;
    if (this.form.valid) {
      this.showError = false;

      if (this.router.url === '/healthcheck/healthplan') {
        this.sharedStorageService.set('reserveType', ID_LOGIN_TYPE.PERSONAL);
        const userPlanRequest = {
          orgNo: '01', // 活動固定帶敦南
          gender: this.form.get('gender')!.value!,
          birthday: this.form
            .get('birthday')!
            .value!.format(API_DATE_FORMAT)!,
          age: this.age,
          idNo: '',
          uuid: '',
          name: '',
          cell: '',
          email: '',
          telH: '',
          telO: '',
          zipCode: '',
          history: [],
          family: [],
          subjectiveSymptoms: [],
          identityType: '0',
          funcType: '1',
          femaleStatus: false,
          femaleMenopausal: false,
        }
        this.sharedStorageService.set('userPlanRequest', userPlanRequest);
      }

      // 若選擇女性且勾選懷孕，則顯示提示對話框並不能往下預約
      if (
          this.form.get('gender')?.value === API_PARAMS.GENDER.FEMALE &&
          this.form.get('status')?.value === API_PARAMS.FEMALE_STATUS.PREGNANCY
        ) {
        const femalePregnancyHintDialog = this.dialog.open(
          SingleActionDialogComponent,
          {
            data: {
              title: this.translateService.instant(
                'reserve-info.female-pregnancy-hint-title'
              ),
              message: this.translateService.instant(
                'reserve-info.female-pregnancy-hint-message'
              ),
              buttonText: this.translateService.instant('not-found.back-to-home'),
              showCloseButton: true,
              action: () => {
                femalePregnancyHintDialog.close();
                this.sharedStorageService.clear();
                this.router.navigate(['/healthcheck']).then();
              },
            },
          }
        );
        return;
      }

      const userPlanRequest = this.sharedStorageService.get('userPlanRequest');
      const businessPlanRequest = this.sharedStorageService.get('businessPlanRequest');
      if (userPlanRequest) {
        userPlanRequest.gender = this.form.get('gender')!.value!;
        userPlanRequest.birthday = this.form
          .get('birthday')!
          .value!.format(API_DATE_FORMAT)!;
        userPlanRequest.age = this.age;
        userPlanRequest.femaleStatus = false;
        userPlanRequest.femaleMenopausal = false;
        if (this.isFemale) {
          userPlanRequest.femaleStatus =
            this.form.get('status')?.value === 'true';
          userPlanRequest.femaleMenopausal =
            this.form.get('menopausal')?.value === 'true';
        }
        this.sharedStorageService.set('userPlanRequest', userPlanRequest);
      }

      if (businessPlanRequest) {
        businessPlanRequest.gender = this.form.get('gender')!.value!;
        this.sharedStorageService.set('businessPlanRequest', businessPlanRequest);
      }

      this.sharedStorageService.set('hasFilledReserveInfo', true);

      this.router.navigate(['/healthcheck/healthplan/reserve/personalhistory']).then();
    }
  }

  protected readonly API_PARAMS = API_PARAMS;
  protected readonly DATE_FORMAT = DATE_FORMAT;
}
